// Koordinatrutenett: adaptivt lat/lon-graticule med MGRS-etiketter i
// skjæringspunktene. Regenereres per viewport (moveend). Tetthet velges av
// zoom. (Et fullt UTM-alignet MGRS-rutenett er en senere forfining; dette gir
// et ekte, lesbart referansenett med MGRS-koordinater.)
import type { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import { forward as mgrsForward } from 'mgrs';
import { featureCollection, type GeoFeature } from '@skade/contracts';

/** Grader mellom rutelinjer ved gitt zoom. */
function stepForZoom(z: number): number {
  if (z >= 14) return 0.01;
  if (z >= 12) return 0.02;
  if (z >= 10) return 0.05;
  if (z >= 8) return 0.1;
  if (z >= 6) return 0.5;
  return 1;
}

function snap(v: number, step: number): number {
  return Math.floor(v / step) * step;
}

export class Graticule {
  // Rutenett er AV som standard — det ble for rotete på kartet. Slås på via
  // toggle() (verktøylinja), som da bygger nettet for gjeldende viewport.
  private visible = false;
  constructor(private map: MlMap) {
    map.on('load', () => {
      this.init();
      this.rebuild();
      map.on('moveend', () => this.rebuild());
    });
  }

  private init() {
    this.map.addSource('grid', { type: 'geojson', data: featureCollection([]) });
    this.map.addLayer({
      id: 'grid-line', type: 'line', source: 'grid',
      filter: ['==', '$type', 'LineString'],
      layout: { visibility: 'none' },
      paint: { 'line-color': '#2d3e28', 'line-width': 1, 'line-opacity': 0.6 },
    });
    this.map.addLayer({
      id: 'grid-label', type: 'symbol', source: 'grid',
      filter: ['==', '$type', 'Point'],
      layout: {
        visibility: 'none',
        'text-field': ['get', 'mgrs'], 'text-font': ['Open Sans Regular'],
        'text-size': 10, 'text-anchor': 'bottom-left', 'text-offset': [0.3, -0.3],
      },
      paint: { 'text-color': '#5b6b5b', 'text-halo-color': '#0a0c0a', 'text-halo-width': 1 },
    });
  }

  /** Slår rutenettet av/på. Returnerer NY tilstand, så kalleren kan vise den. */
  toggle(): boolean {
    this.visible = !this.visible;
    const v = this.visible ? 'visible' : 'none';
    this.map.setLayoutProperty('grid-line', 'visibility', v);
    this.map.setLayoutProperty('grid-label', 'visibility', v);
    if (this.visible) this.rebuild(); // bygg nettet for gjeldende viewport når det slås på
    return this.visible;
  }

  isVisible() { return this.visible; }

  private rebuild() {
    if (!this.visible) return;
    const b = this.map.getBounds();
    const step = stepForZoom(this.map.getZoom());
    const feats: GeoFeature[] = [];
    const w = b.getWest(), e = b.getEast(), s = b.getSouth(), n = b.getNorth();

    for (let lon = snap(w, step); lon <= e; lon += step) {
      feats.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: [[lon, s], [lon, n]] }, properties: {} });
    }
    for (let lat = snap(s, step); lat <= n; lat += step) {
      feats.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: [[w, lat], [e, lat]] }, properties: {} });
      for (let lon = snap(w, step); lon <= e; lon += step) {
        try {
          feats.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [lon, lat] },
            properties: { mgrs: mgrsForward([lon, lat], 3) },
          });
        } catch {
          /* utenfor UTM-sonene */
        }
      }
    }
    (this.map.getSource('grid') as GeoJSONSource).setData(featureCollection(feats) as never);
  }
}
